<script setup lang="ts">
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton";
import Tag from "primevue/tag";
import { computed, ref, toRefs } from "vue";
import { useToast } from "vue-toast-notification";
import AddOnConfirmAddModal from "@/components/StudentFulfillment/components/modals/AddOnConfirmAddModal/index.vue";
import AddOnStatusChangeModal from "@/components/StudentFulfillment/components/modals/AddOnStatusChangeModal/index.vue";
import AlertCircleIcon from "@/components/shared/icons/AlertCircleIcon.vue";
import ChevronDownIcon from "@/components/shared/icons/ChevronDownIcon.vue";
import ClockIcon from "@/components/shared/icons/ClockIcon.vue";
import { hasOrdersWriteEvents, hasOrdersWritePermission, showAddOnsInFulfillment } from "@/composables/authorization";
import { ERROR_TIMEOUT, ORDER_PRODUCT_STATUS, ORDER_PRODUCT_TYPE } from "@/constants";
import { useArchiveAddOnRequest } from "@/providers/addOns/useArchiveAddOnRequest";
import { useUnarchiveAddOnRequest } from "@/providers/addOns/useUnarchiveAddOnRequest";
import { useUpdateAddOnRequestStatus } from "@/providers/addOns/useUpdateAddOnRequestStatus";
import { useCreateOrderLineItem } from "@/providers/orders/useCreateOrderLineItem";
import AddOnRequestRow from "./AddOnRequestRow.vue";
import {
  ADD_ON_ADD_ERROR_MESSAGE,
  ADD_ON_REQUESTS_ERROR_MESSAGE,
  ADD_ON_STATUS_UPDATE_ERROR_MESSAGE,
} from "./constants";
import { addOnRequestsPresets, BADGE_COLOR_CLASSES } from "./styles";
import type { AddOnDropdownOption, AddOnRequestItem, BadgeState } from "./types";

defineOptions({ name: "AddOnRequests" });

const props = defineProps<{
  orderId?: string;
  items: AddOnRequestItem[];
  loading: boolean;
  error: boolean;
}>();

const emit = defineEmits<{
  (e: "order-updated", order: unknown): void;
  (e: "retry"): void;
  (e: "item-added", item: AddOnRequestItem): void;
}>();

const activeIndex = ref<number | null | undefined>(0);

const { items, loading, error } = toRefs(props);

const canEditStatus = computed(() => Boolean(hasOrdersWritePermission.value || hasOrdersWriteEvents.value));
const canArchive = computed(() => hasOrdersWriteEvents.value);

const selectedItem = ref<AddOnRequestItem | undefined>(undefined);
const pendingStatus = ref<AddOnDropdownOption | undefined>(undefined);
const restoringItemId = ref<string | undefined>(undefined);
const statusModalOpen = ref(false);
const confirmAddModalOpen = ref(false);

const toast = useToast();
const { mutateAsync: updateRequestStatus, isPending: isUpdatingStatus } = useUpdateAddOnRequestStatus();
const { mutateAsync: archiveRequestMutation, isPending: isArchiving } = useArchiveAddOnRequest();
const { mutateAsync: unarchiveRequestMutation } = useUnarchiveAddOnRequest();
const { mutateAsync: createOrderLineItem, isPending: savingAddToOrder } = useCreateOrderLineItem();

const isSavingStatus = computed(() => isUpdatingStatus.value || isArchiving.value);

const showErrorToast = (message: string): void => {
  toast.open({
    message,
    type: "error",
    position: "bottom",
    duration: ERROR_TIMEOUT,
  });
};

const openStatusModal = (item: AddOnRequestItem, status: AddOnDropdownOption): void => {
  selectedItem.value = item;
  pendingStatus.value = status;
  statusModalOpen.value = true;
};

const closeStatusModal = (): void => {
  statusModalOpen.value = false;
  selectedItem.value = undefined;
  pendingStatus.value = undefined;
};

const confirmStatusChange = async (): Promise<void> => {
  if (!selectedItem.value || !pendingStatus.value) return;
  const targetStatus = pendingStatus.value;
  try {
    if (targetStatus === "Archived") {
      await archiveRequestMutation(selectedItem.value.id);
    } else {
      await updateRequestStatus({ addOnRequestId: selectedItem.value.id, requestedStatus: targetStatus });
    }
  } catch {
    showErrorToast(`Couldn't update status to ${targetStatus}. ${ADD_ON_STATUS_UPDATE_ERROR_MESSAGE}`);
  } finally {
    closeStatusModal();
  }
};

const openConfirmAddModal = (item: AddOnRequestItem): void => {
  selectedItem.value = item;
  confirmAddModalOpen.value = true;
};

const closeConfirmAddModal = (): void => {
  confirmAddModalOpen.value = false;
  selectedItem.value = undefined;
};

const confirmAddToOrder = async (): Promise<void> => {
  if (!selectedItem.value || !props.orderId) return;
  const addedItem = selectedItem.value;
  try {
    const response = await createOrderLineItem({
      orderId: props.orderId,
      payload: {
        new_product_id: addedItem.productId,
        status: ORDER_PRODUCT_STATUS.fulfilled,
        product_type: ORDER_PRODUCT_TYPE.event,
      },
    });
    emit("item-added", addedItem);
    emit("order-updated", response.data);
  } catch {
    showErrorToast(`Couldn't add the excursion to the order. ${ADD_ON_ADD_ERROR_MESSAGE}`);
  } finally {
    closeConfirmAddModal();
  }
};

const restoreRequest = async (item: AddOnRequestItem): Promise<void> => {
  restoringItemId.value = item.id;
  try {
    await unarchiveRequestMutation(item.id);
  } catch {
    showErrorToast(`Couldn't update status to ${item.status}. ${ADD_ON_STATUS_UPDATE_ERROR_MESSAGE}`);
  } finally {
    restoringItemId.value = undefined;
  }
};

const pendingCount = computed(() => items.value.filter((item) => !item.addedToOrder && !item.archived).length);
const addedCount = computed(() => items.value.filter((item) => item.addedToOrder).length);

const badgeState = computed<BadgeState>(() => {
  if (loading.value) return "loading";
  if (error.value) return "error";
  if (pendingCount.value > 0) return "pending";
  if (addedCount.value > 0) return "added";
  return "archived";
});

const badgeLabel = computed(() => {
  switch (badgeState.value) {
    case "loading":
      return "LOADING";
    case "error":
      return "UNAVAILABLE";
    case "pending":
      return `${pendingCount.value} PENDING`;
    case "added":
      return `${addedCount.value} ADDED`;
    case "archived":
      return "ALL ARCHIVED";
  }
});

const badgeColorClass = computed(() => BADGE_COLOR_CLASSES[badgeState.value]);

const showSection = computed(
  () => showAddOnsInFulfillment.value && (loading.value || error.value || items.value.length > 0),
);
</script>

<template>
  <div v-if="showSection" class="border border-gray-175 rounded-lg shadow-xs overflow-hidden mb-3 mt-3" data-testid="add-on-requests-section">
    <Accordion v-model:activeIndex="activeIndex">
      <AccordionTab :pt="addOnRequestsPresets.accordionTab">
        <template #headericon></template>
        <template #header>
          <span class="flex w-8 h-8 rounded-lg bg-cream-100 items-center justify-center flex-none">
            <ClockIcon class="h-4 w-4 text-warning-900" />
          </span>
          <ChevronDownIcon
            class="h-4 w-4 text-[#8A6D3B] transition-transform flex-none"
            :class="{ '-rotate-90': activeIndex !== 0 }"
          />
          <span class="text-15p font-bold tracking-wide text-indigo-base normal-case">Add-on Requests</span>
          <Tag
            rounded
            :value="badgeLabel"
            :class="['flex-none whitespace-nowrap !text-1xs !font-bold !tracking-wide !text-white', badgeColorClass]"
          />
          <span class="ml-auto text-13p font-normal text-[#8A6D3B] normal-case hidden md:inline">
            Change the status or add a requested excursion to the student's order.
          </span>
        </template>

        <div class="p-5 flex flex-col gap-3.5">
          <template v-if="loading">
            <div class="flex items-start gap-5 p-5 border border-gray-175 rounded-lg">
              <div class="flex-1 flex flex-col gap-3">
                <Skeleton width="15rem" height="1.1rem" />
                <Skeleton width="26rem" height="0.8rem" />
                <div class="flex gap-4">
                  <Skeleton width="11rem" height="0.8rem" />
                  <Skeleton width="7rem" height="0.8rem" />
                </div>
              </div>
              <div class="flex flex-col items-end gap-3.5">
                <Skeleton width="9rem" height="2.1rem" />
                <Skeleton width="10.5rem" height="2.9rem" />
              </div>
            </div>
          </template>

          <div v-else-if="error" class="flex flex-col items-center justify-center gap-3 py-8 px-5">
            <span class="flex w-12 h-12 rounded-full bg-error-100 items-center justify-center">
              <AlertCircleIcon color="#D11313" :size="22" />
            </span>
            <div class="text-center">
              <p class="text-base font-bold text-indigo-base">Something went wrong</p>
              <p class="text-sm text-gray-600 mt-1 max-w-md">{{ ADD_ON_REQUESTS_ERROR_MESSAGE }}</p>
            </div>
            <Button
              data-testid="add-on-requests-retry-button"
              :pt="addOnRequestsPresets.primaryButton"
              @click="emit('retry')"
            >
              Retry
            </Button>
          </div>

          <template v-else>
            <AddOnRequestRow
              v-for="item in items"
              :key="item.id"
              :item="item"
              :can-edit-status="canEditStatus"
              :can-archive="canArchive"
              :is-restoring="restoringItemId === item.id"
              @change-status="(status) => openStatusModal(item, status)"
              @add-to-order="openConfirmAddModal(item)"
              @restore="restoreRequest(item)"
            />
          </template>
        </div>
      </AccordionTab>
    </Accordion>

    <AddOnStatusChangeModal
      v-if="statusModalOpen"
      :visible="statusModalOpen"
      :item="selectedItem"
      :target-status="pendingStatus"
      :saving="isSavingStatus"
      @update:visible="closeStatusModal"
      @confirm="confirmStatusChange"
    />
    <AddOnConfirmAddModal
      v-if="confirmAddModalOpen"
      :visible="confirmAddModalOpen"
      :item="selectedItem"
      :saving="savingAddToOrder"
      @update:visible="closeConfirmAddModal"
      @confirm="confirmAddToOrder"
    />
  </div>
</template>
